import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from '@gem/modules/i18n';
import { ICommonPopupsProps, PopupStore } from '@gem/popups';

import Tasks from 'APP/Tasks';
import { RouterParams } from 'APP/router/constants';
import Entities from 'STORE';

import { ForwardMessageWithInfoPopup } from '../ForwardMessageWithInfoPopup/ForwardMessageWithInfoPopup';
import { IMeetingLinkPopupProps } from './MeetingLinkPopup.types';

interface INewMeetingPopupPresenter {
  onForwardLink: () => void;
  onClose: () => void;
  onCopy: () => void;
  tryToClose: () => void;
  onJoinCall: () => void;
  isCopy: boolean;
  childPopup: PopupStore | null;
}

export function useMeetingLinkPopupPresenter(
  data: ICommonPopupsProps<IMeetingLinkPopupProps>
): INewMeetingPopupPresenter {
  const { popupInstance } = data;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const presenter = useLocalObservable<INewMeetingPopupPresenter>(() => ({
    isCopy: false,
    childPopup: null,

    async onJoinCall(): Promise<void> {
      popupInstance.close();
      if (Entities.Calls.ongoingCall) {
        const result = await Tasks.calls.askAndDeleteActiveCall();
        if (result) {
          navigate(`?${RouterParams.roomCallChannelId}=${data.params!.channelId}`, {
            replace: true,
          });
        }
      } else {
        navigate(`?${RouterParams.roomCallChannelId}=${data.params!.channelId}`, {
          replace: true,
        });
      }
    },

    onClose(): void {
      presenter.tryToClose();
    },

    onCopy(): void {
      presenter.isCopy = true;
    },

    onForwardLink(): void {
      popupInstance.hide();

      presenter.childPopup = Tasks.app.openPopup(ForwardMessageWithInfoPopup, {
        params: {
          title: t('new_meeting_modal_send_invitation_link'),
          info: data.params?.link || '',
          showToastCallback: () => {
            Tasks.app.showToast(t('meeting_link_sent'));
          },
        },
        onClose: () => popupInstance.show(),
      });
    },

    async tryToClose(): Promise<void> {
      let canClose = true;

      if (!presenter.isCopy) {
        canClose = await Tasks.app.showConfirm({
          text: t('new_meeting_actions_modal_closing_confirmation_message'),
        });
      }

      if (canClose) {
        popupInstance.close();
      }
    },
  }));

  useEffect(() => {
    return () => presenter.childPopup?.close();
  }, []);

  return presenter;
}
